import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { PopupV2 } from './PopupV2';
import ButtonGeneral from './ButtonGeneral';
import { StreakBadge } from '../../resources/svgComponents/badgeComponents/StreakBadge';
import { useSignedInContext } from '../../context/SignedInContext';
import { useUpsertStreakHistoryMutation } from '../../graphql/journey/useUpsertStreakHistoryMutation.gql';

export default function StreakModal() {
  const { streakHistory, user, refetchStreakHistory } = useSignedInContext();
  const [showBadgeUnlocked, setShowBadgeUnlocked] = useState(false);
  const [streakCount, setStreakCount] = useState<number | null>(null);
  const [upsertStreakHistory] = useUpsertStreakHistoryMutation();

  const isMobile = Platform.OS !== 'web';

  // 🔍 Find the lowest unseen streak every time history changes
  const nextUnseen = useMemo(() => {
    const unseen = streakHistory.filter(h => !h.seen).sort((a, b) => a.streakCount - b.streakCount);
    return unseen.length > 0 ? unseen[0] : null;
  }, [streakHistory]);

  useEffect(() => {
    if (nextUnseen) {
      setStreakCount(nextUnseen.streakCount);
      setShowBadgeUnlocked(true);
    } else {
      setShowBadgeUnlocked(false);
      setStreakCount(null);
    }
  }, [nextUnseen]);

  const handleDone = async () => {
    if (streakCount && user?.id && nextUnseen) {
      try {
        await upsertStreakHistory({
          variables: {
            userId: user.id,
            data: {
              streakCount,
              earnedAt: nextUnseen.earnedAt, // ✅ keep original earnedAt
              seen: true,
            },
          },
        });
      } catch (err) {
        console.error('❌ Failed to mark streak seen:', err);
      }
    }
    setShowBadgeUnlocked(false);
  };

  if (!streakCount) return null; // nothing to show

  return (
    <PopupV2
      title="Badge Unlocked"
      visible={showBadgeUnlocked}
      setVisible={setShowBadgeUnlocked}
      height={isMobile ? 400 : 438}
      hexagonHeight={isMobile ? 54 : 62}
      hexagonWidth={isMobile ? 252 : 346}
      hideCloseButton={true}
      closeOnBackdropPress={false}
    >
      <View style={[styles.card]}>
        <View style={[styles.content, { marginTop: isMobile ? 33 : 26 }]}>
          <Text style={[styles.subtitle, { fontSize: isMobile ? 18 : 24 }]}>Congratulations</Text>
          <Text style={[styles.description, { fontSize: isMobile ? 14 : 16 }]}>
            You have earned a badge
          </Text>
          <StreakBadge number={streakCount} />
        </View>

        <View style={[styles.buttonContainer, { height: isMobile ? 80 : 102 }]}>
          <ButtonGeneral
            onPress={handleDone}
            title="Done"
            height={48}
            width={isMobile ? 146 : 411}
            fontSize={isMobile ? 14 : 16}
          />
        </View>
      </View>
    </PopupV2>
  );
}

const styles = StyleSheet.create({
  card: {
    overflow: 'hidden',
    flexDirection: 'column',
    height: '100%',
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 60,
    paddingTop: 30,
    flex: 1,
  },
  subtitle: {
    fontFamily: 'Secular One',
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 8,
    color: '#000000',
  },
  description: {
    fontFamily: 'Secular One',
    color: '#453B3B',
    textAlign: 'center',
    fontWeight: '700',
    marginBottom: 20,
  },
  buttonContainer: {
    justifyContent: 'center',
    backgroundColor: '#FEDD88',
    alignItems: 'center',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
});
